import { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { useApi } from "../../hooks/useApi";

const WHITELIST_IPS = ['63.184.161.247', '88.119.254.47', '78.63.145.76', '217.117.31.74'];

// Ordered as the CoC Allowed API Endpoints screen presents them — by section, then down each
// column before moving right — so the operator can tick straight down the list without hunting.
const ALLOWED_ENDPOINTS = [
  'Update Order',
  'Query Orders',
  'Refund Order',
  'Cancel Order',
  'Update Fulfillment',
  'Query Customers',
  'Query Customer History',
  'Query Transactions',
  'Update Transactions / Mark Chargeback',
  'Refund Transaction',
  'Transaction Range Select',
  'Query Purchases',
  'Cancel Purchase',
  'Refund Purchase',
  'Query Products',
  'Query Gateways',
];

const PASSWORD_GENERATOR_URL =
  'https://www.random.org/strings/?num=10&len=32&digits=on&upperalpha=on&loweralpha=on&unique=on&format=html&rnd=new';

const INSTRUCTIONS_TEXT = [
  'How to create the Checkout Champ API user',
  '',
  '1. Go to Admin → Users',
  '2. Click Add User (the + icon)',
  '3. Username: chargemont.api.[client_name]  (e.g. chargemont.api.nike)',
  '4. Access Level: Api User',
  '5. Whitelist these IPs:',
  ...WHITELIST_IPS.map((ip) => `     - ${ip}`),
  '6. Password: generate a secure password, or use one of genenerated by this generator:',
  `     ${PASSWORD_GENERATOR_URL}`,
  '7. Allowed API Endpoints:',
  ...ALLOWED_ENDPOINTS.map((endpoint) => `     - ${endpoint}`),
  '8. Click the Create button',
].join('\n');

function Mono({ children }: { children: React.ReactNode }) {
  return (
    <Box
      component="code"
      sx={{
        fontFamily: 'monospace',
        fontSize: '0.85em',
        backgroundColor: '#ececf0',
        px: 0.5,
        py: 0.1,
        borderRadius: 0.5,
      }}
    >
      {children}
    </Box>
  );
}

interface CheckoutChampDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  apiUrl: string;
}

export default function CheckoutChampDialog({
  open,
  onClose,
  onSuccess,
  apiUrl,
}: CheckoutChampDialogProps) {
  const { post } = useApi();
  const [formData, setFormData] = useState({ name: '', loginId: '', password: '' });
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopyInstructions = async () => {
    await navigator.clipboard.writeText(INSTRUCTIONS_TEXT);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const resetAndClose = () => {
    setFormData({ name: '', loginId: '', password: '' });
    setFormError(null);
    onClose();
  };

  const handleDialogClose = (_event: object, reason: string) => {
    if (reason !== 'backdropClick') {
      resetAndClose();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setFormError(null);
  };

  const handleCreate = async () => {
    setFormError(null);
    setSubmitting(true);

    try {
      await post(apiUrl, {
        name: formData.name,
        loginId: formData.loginId,
        password: formData.password,
      }, { slowApi: true });

      resetAndClose();
      await onSuccess();
    } catch (err: any) {
      setFormError(err.message || 'Failed to link Checkout Champ account');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <DialogTitle>Link Checkout Champ Account</DialogTitle>
      <DialogContent>
        <Accordion
          disableGutters
          elevation={0}
          sx={{
            mb: 2,
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            overflow: 'hidden',
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ backgroundColor: '#f5f5f7', '& .MuiAccordionSummary-content': { my: 1 } }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                pr: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                How to create the API user
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={
                  copied ? (
                    <CheckIcon fontSize="small" sx={{ color: 'success.main' }} />
                  ) : (
                    <ContentCopyIcon fontSize="small" />
                  )
                }
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopyInstructions();
                }}
                sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
              >
                {copied ? 'Copied!' : 'Copy Instructions'}
              </Button>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 1 }}>
            <Box
              component="ol"
              sx={{
                m: 0,
                pl: 2.5,
                color: 'text.secondary',
                fontSize: 14,
                lineHeight: 1.6,
                '& > li': { mb: 1 },
                '& ul': { mt: 0.5, mb: 0, pl: 2.5 },
                '& ul > li': { mb: 0.25 },
              }}
            >
              <li>
                Go to <strong>Admin → Users</strong>
              </li>
              <li>
                Click <strong>Add User</strong> (the + icon)
              </li>
              <li>
                <strong>Username:</strong> <Mono>chargemont.api.[client_name]</Mono> — e.g.{' '}
                <Mono>chargemont.api.nike</Mono>
              </li>
              <li>
                <strong>Access Level:</strong> Api User
              </li>
              <li>
                <strong>Whitelist</strong> these IPs:
                <Box component="ul">
                  {WHITELIST_IPS.map((ip) => (
                    <li key={ip}>
                      <Mono>{ip}</Mono>
                    </li>
                  ))}
                </Box>
              </li>
              <li>
                <strong>Password:</strong> generate a secure password, or use one of genenerated by the{' '}
                <Link href={PASSWORD_GENERATOR_URL} target="_blank" rel="noopener noreferrer">
                  random.org generator
                </Link>
              </li>
              <li>
                <strong>Allowed API Endpoints:</strong>
                <Box component="ul">
                  {ALLOWED_ENDPOINTS.map((endpoint) => (
                    <li key={endpoint}>{endpoint}</li>
                  ))}
                </Box>
              </li>
              <li>
                Click the <strong>Create</strong> button
              </li>
            </Box>
          </AccordionDetails>
        </Accordion>

        {formError && (
          <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
            {formError}
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Account Name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            fullWidth
            required
            disabled={submitting}
            size="small"
          />
          <TextField
            label="Login ID"
            name="loginId"
            value={formData.loginId}
            onChange={handleInputChange}
            fullWidth
            required
            disabled={submitting}
            size="small"
          />
          <TextField
            label="Password"
            name="password"
            type="password"
            value={formData.password}
            onChange={handleInputChange}
            fullWidth
            required
            disabled={submitting}
            size="small"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={resetAndClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={submitting || !formData.name || !formData.loginId || !formData.password}
          sx={{
            backgroundColor: '#465fff',
            '&:hover': {
              backgroundColor: '#3641f5',
            },
          }}
        >
          {submitting ? 'Verifying...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
